import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, ChevronRight, Menu, X, FileText, CheckCircle, Phone, FileBarChart, Calendar, Users, Receipt } from 'lucide-react';

// Routes where the floating nav should NOT appear
const HIDDEN_ROUTES = [
  '/',
  '/home',
  '/manager-login',
  '/print-work-report',
  '/print-customer-report',
  '/privacy',
  '/terms',
];

const QUICK_LINKS = [
  { label: 'דף הבית', to: '/home', icon: Home, color: 'from-blue-600 to-indigo-600' },
  { label: 'רישום יומי', to: '/daily-log', icon: FileText, color: 'from-indigo-600 to-purple-600' },
  { label: 'סגירת עסקה', to: '/deal-closure', icon: CheckCircle, color: 'from-green-600 to-emerald-600' },
  { label: 'קריאות שירות', to: '/support-tickets', icon: Phone, color: 'from-cyan-600 to-blue-600' },
  { label: 'דווח', to: '/reports', icon: FileBarChart, color: 'from-purple-600 to-pink-600' },
  { label: 'חשבונות מרוכזים', to: '/consolidated-invoices', icon: Receipt, color: 'from-teal-600 to-cyan-600' },
  { label: 'יומן', to: '/daily-journal', icon: Calendar, color: 'from-pink-500 to-rose-500' },
  { label: 'לקוחות', to: '/customers', icon: Users, color: 'from-teal-600 to-cyan-600' },
];

export const FloatingHomeButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (HIDDEN_ROUTES.includes(location.pathname) || location.hash?.includes('session_id=')) {
    return null;
  }

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <style>{`
        @keyframes float-nav-in {
          0% { transform: translate(-100%, -50%); opacity: 0; }
          60% { transform: translate(8%, -50%); opacity: 1; }
          100% { transform: translate(0, -50%); opacity: 1; }
        }
        @keyframes float-nav-pulse {
          0%, 100% { box-shadow: 0 10px 25px -5px rgba(37,99,235,0.5); }
          50% { box-shadow: 0 10px 35px 0px rgba(79,70,229,0.75); }
        }
        @keyframes float-link-in {
          0% { transform: translateX(-20px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .float-nav-group { animation: float-nav-in 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .float-nav-toggle { animation: float-nav-pulse 2.8s ease-in-out infinite; animation-delay: 0.7s; }
        .float-link { animation: float-link-in 0.3s ease-out both; }
      `}</style>
      <div
        className="float-nav-group fixed left-0 top-1/2 z-[9999] print:hidden flex flex-col items-start gap-2"
        style={{ transform: 'translateY(-50%)' }}
        data-testid="floating-nav-group"
      >
        {/* Toggle button */}
        <button
          onClick={() => setOpen(o => !o)}
          title={open ? 'סגור תפריט' : 'תפריט ניווט מהיר'}
          aria-label="תפריט ניווט מהיר"
          data-testid="floating-nav-toggle"
          className={`float-nav-toggle flex flex-col items-center gap-1 pr-3 pl-4 py-4
                     bg-gradient-to-br from-blue-600 to-indigo-600 text-white
                     rounded-r-2xl border-2 border-white/30
                     hover:from-blue-700 hover:to-indigo-700 hover:pl-6
                     transition-all duration-300 group`}
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7 group-hover:scale-110 transition-transform" />}
          <span className="text-xs font-bold leading-tight">{open ? 'סגור' : 'תפריט'}</span>
        </button>

        {/* Quick links (expanded) */}
        {open && (
          <div className="flex flex-col gap-1.5" data-testid="floating-nav-menu">
            {QUICK_LINKS.map((link, idx) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.to}
                  onClick={() => go(link.to)}
                  title={link.label}
                  data-testid={`floating-nav-link-${link.to.replace('/', '')}`}
                  className={`float-link flex items-center gap-2 pr-3 pl-4 py-2.5
                             bg-gradient-to-br ${link.color} text-white
                             rounded-r-xl border border-white/20 shadow-lg
                             hover:pl-6 transition-all duration-300 min-w-[140px]`}
                  style={{ animationDelay: `${idx * 40}ms` }}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-semibold whitespace-nowrap">{link.label}</span>
                </button>
              );
            })}
            <button
              onClick={() => { setOpen(false); navigate(-1); }}
              title="חזרה לעמוד הקודם"
              data-testid="floating-back-button"
              className="float-link flex items-center gap-2 pr-3 pl-4 py-2.5
                         bg-white/95 backdrop-blur text-indigo-700
                         rounded-r-xl border border-indigo-200 shadow-lg
                         hover:pl-6 transition-all duration-300 min-w-[140px]"
              style={{ animationDelay: `${QUICK_LINKS.length * 40}ms` }}
            >
              <ChevronRight className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-semibold whitespace-nowrap">חזרה לעמוד הקודם</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingHomeButton;
